import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { PiggyBank, Leaf, Sprout, Users, Loader2, Share2 } from "lucide-react";
import { toast } from "sonner";
import { formatEuro } from "@/lib/dates";
import { generateImpactBlob } from "@/lib/badge";

const frNum = (n) => (n || 0).toLocaleString("fr-FR", { maximumFractionDigits: 1 });

export default function ImpactPanel() {
  const { user } = useAuth();
  const [impact, setImpact] = useState(null);
  const [refCode, setRefCode] = useState(null);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    (async () => {
      const [imp, ref] = await Promise.all([
        api.get("/impact"),
        api.get("/referral").catch(() => ({ data: null })),
      ]);
      setImpact(imp.data);
      setRefCode(ref.data?.code || null);
    })();
  }, []);

  const shareImpact = async () => {
    setSharing(true);
    try {
      const refUrl = refCode ? `${window.location.origin}/connexion?ref=${refCode}` : "";
      const blob = await generateImpactBlob(impact, user?.name, refUrl);
      const file = new File([blob], "impact-foodfend.png", { type: "image/png" });
      const text = `Avec FoodFend, j'ai sauvé ${impact.saved} produits, économisé ${formatEuro(
        impact.money_saved
      )} et évité ${impact.co2_saved} kg de CO₂ ! 🌱${refUrl ? " " + refUrl : ""}`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Mon impact anti-gaspi", text, url: refUrl });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "impact-foodfend.png";
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Impact enregistré en image !");
      }
    } catch (err) {
      if (err?.name !== "AbortError") toast.error("Impossible de générer l'image.");
    } finally {
      setSharing(false);
    }
  };

  if (impact === null)
    return <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>;

  const cards = [
    { key: "money", label: "Économisé au total", value: formatEuro(impact.money_saved), icon: PiggyBank, tone: "text-primary" },
    { key: "co2", label: "CO₂ évité (estim.)", value: `${frNum(impact.co2_saved)} kg`, icon: Leaf, tone: "text-primary" },
    { key: "saved", label: "Produits sauvés", value: impact.saved, icon: Sprout, tone: "text-foreground" },
    { key: "referrals", label: "Amis parrainés", value: impact.referrals, icon: Users, tone: "text-[hsl(var(--warning))]" },
  ];

  return (
    <div className="space-y-6" data-testid="impact-panel">
      <div className="rounded-2xl border border-border bg-card p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <p className="mono text-xs uppercase tracking-mono text-primary mb-1">Mon impact anti-gaspi</p>
        <h3 className="font-display text-2xl sm:text-3xl leading-tight">
          Vous avez sauvé <span className="text-primary">{impact.saved}</span> produit(s)
          {impact.kg_saved > 0 && (
            <> — soit environ <span className="text-primary">{frNum(impact.kg_saved)} kg</span> de nourriture</>
          )}.
        </h3>
        <p className="text-muted-foreground mt-2">
          Chaque produit consommé plutôt que jeté, c’est de l’argent et du CO₂ économisés.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map(({ key, label, value, icon: Icon, tone }) => (
          <div key={key} className="bg-card border border-border rounded-2xl p-4 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
            data-testid={`impact-${key}`}>
            <div className="flex items-center justify-between">
              <span className="mono text-[10px] uppercase tracking-mono text-muted-foreground">{label}</span>
              <Icon className={`h-4 w-4 ${tone}`} />
            </div>
            <p className={`font-mono font-bold text-2xl mt-2 ${tone}`}>{value}</p>
          </div>
        ))}
      </div>

      <div className="flex justify-center">
        <Button className="rounded-full" onClick={shareImpact} disabled={sharing}
          data-testid="share-impact-btn">
          {sharing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Share2 className="h-4 w-4 mr-1.5" />}
          Partager mon impact
        </Button>
      </div>
    </div>
  );
}
